/* eslint-disable quotes */
import moment from 'moment';
import _ from 'lodash';
import {
  displayPriceFormat,
  getWebViewCustomerId,
  getWebViewPageId,
  isQrCode,
  isStringNotNull,
  sendOrderClick,
  showMessageModalWithLoading,
} from '../../utils';
import {
  API_SEND_ORDER_MESSENGER,
  ORDER_TYPE_CLICK_AND_COLLECT,
  ORDER_TYPE_DELIVERY,
  PAYMENT,
  STRIPE_MINIMUM_AMOUNT,
  TIME_SLOT_UNAVAILABLE_MESSAGE,
  EMPTY_CART_ERROR,
  ZERO_ORDER_ERROR,
  MINIMUM_ORDER_DELIVERY_ERROR,
  MINIMUM_ORDER_CLICK_AND_COLLECT_ERROR,
  MINIMUM_STRIPE_PAYMENT_ERROR,
  PAYMENT_NOT_AVAILABLE,
} from '../../constants';
import { ERROR_NAMES } from '../../utils/error';
import { showCartModal, showMessageModal } from '../../redux/actions';
import store from '../../redux/store';
import { closeWebView, sendPostRequest } from '../../api';
import { fetchShopOrderSlots } from '../../api/shops';
import { getCompanyChatbotLink } from '../../api/company';
import { logHighError } from '../../utils/logger';
import { getFirebaseCustomerId } from '../../api/firebase/firebaseAuthentication';
import { getTimezone } from '../../utils/date';

export const getMaximumPreparationTime = (commande) => {
  let maxPreparationTime = 0;
  _.map(commande, (product) => {
    const { preparationTime } = product;
    if (maxPreparationTime < parseInt(preparationTime, 10)) {
      maxPreparationTime = parseInt(preparationTime, 10);
    }
  });
  return maxPreparationTime;
};

export const getOrderTimes = async (day = moment()) => {
  const customerId = getFirebaseCustomerId();
  const timezone = getTimezone();

  const { companyId } = store.getState().configurationReducer;
  const availableDates = await fetchShopOrderSlots(
    companyId,
    customerId,
    day,
    timezone,
  );

  return availableDates;
};

export const sendOrderMessenger = async (setLoading) => {
  try {
    setLoading(true);
    const { companyId } = store.getState().configurationReducer;
    const link = await getCompanyChatbotLink(companyId);
    const customerId = getWebViewCustomerId();
    const pageId = getWebViewPageId();
    const data = {
      customerId,
      pageId,
    };
    await sendPostRequest(API_SEND_ORDER_MESSENGER, data);
    closeWebView();
    setTimeout(() => {
      setLoading(false);
      window.location.href = link;
      store.dispatch(showCartModal(false));
    }, 5000);
  } catch (error) {
    setLoading(false);
    store.dispatch(
      showMessageModal(
        "Nous n'avons pas pu envoyer votre commande. Veuillez réessayer.",
      ),
    );
    logHighError(`Could not send order messenger ${error}`);
  }
};

export const checkOrder = async ({
  paymentType,
  card,
  commande,
  selectTime,
  setLoading,
  isPhoneIncorrect,
  setChoosePayment,
  stripe,
  phoneObj,
  setDateState,
}) => {
  if (!commande || _.isEmpty(commande)) {
    store.dispatch(showCartModal(false));
    showMessageModalWithLoading(setLoading, EMPTY_CART_ERROR);
  } else {
    const { user } = store.getState().userReducer;
    const { numero } = user;
    const {
      minimum,
      minimumClickAndCollect,
      servicePrice: { customAccountId },
    } = store.getState().shopReducer;
    const { orderTime } = store.getState().orderReducer;
    const { orderType, totalPriceOrder, totalProductPriceOrder } =
      store.getState().pendingOrderReducer;

    if (!customAccountId) {
      showMessageModalWithLoading(setLoading, PAYMENT_NOT_AVAILABLE);
      return;
    }
    if (!isQrCode()) {
      const isInvalidOrderTime =
        !orderTime || (orderTime.value && !selectTime && orderTime.disabled);

      if (isInvalidOrderTime) {
        setDateState({ isValidDate: false });
        return;
      }
    }
    setLoading(true);

    if ((isPhoneIncorrect || !isStringNotNull(numero)) && !isQrCode()) {
      setLoading(false);
    } else if (orderType === ORDER_TYPE_DELIVERY && totalPriceOrder < minimum) {
      showMessageModalWithLoading(
        setLoading,
        MINIMUM_ORDER_DELIVERY_ERROR(displayPriceFormat(minimum, false, true)),
      );
    } else if (
      orderType === ORDER_TYPE_CLICK_AND_COLLECT &&
      totalPriceOrder < minimumClickAndCollect
    ) {
      showMessageModalWithLoading(
        setLoading,
        MINIMUM_ORDER_CLICK_AND_COLLECT_ERROR(
          displayPriceFormat(minimumClickAndCollect, false, true),
        ),
      );
    } else if (
      paymentType === PAYMENT.TYPE_STRIPE &&
      totalPriceOrder < STRIPE_MINIMUM_AMOUNT &&
      totalPriceOrder > 0
    ) {
      showMessageModalWithLoading(
        setLoading,
        MINIMUM_STRIPE_PAYMENT_ERROR(
          displayPriceFormat(STRIPE_MINIMUM_AMOUNT, false, true),
        ),
      );
    } else {
      await safeSendOrderClick(
        { paymentType, card, stripe, setLoading, phoneObj, setChoosePayment },
        setDateState,
      );
      setChoosePayment(true);
    }
  }
};

const safeSendOrderClick = async (params, setDateState) => {
  try {
    await sendOrderClick(params);
  } catch (error) {
    if (
      error?.errorName?.includes(ERROR_NAMES.SHOP_SLOT_ERRORS) ||
      String(error).includes(TIME_SLOT_UNAVAILABLE_MESSAGE)
    ) {
      setDateState({
        day: moment(),
        hourSelectValue: 0,
        selectTime: false,
        isValidDate: true,
      });
    }
    throw error;
  }
};

export const getPaymentTypeLabel = (paymentType) => {
  const paymentTypeLabels = {
    [PAYMENT.TYPE_STRIPE]: 'En carte bancaire',
    [PAYMENT.TYPE_CB]: 'En carte bancaire à réception',
    [PAYMENT.TYPE_TO_DRIVER]: 'Paiement au livreur',
    [PAYMENT.TYPE_CASH]: 'En espèces à réception',
    [PAYMENT.TYPE_TICKET_RESTO]: 'En ticket restaurant',
    [PAYMENT.TYPE_COUNTER]: 'Paiement au comptoir',
  };

  return paymentTypeLabels[paymentType];
};

export const getPaymentTypes = () => {
  const { paymentTypes } = store.getState().configurationReducer;
  const { qrcode, delivery } = store.getState().configurationReducer;
  const { shopConfiguration = {} } = store.getState().shopReducer;
  const { orderType, address: customerAddress } =
    store.getState().pendingOrderReducer;
  const { paymentTypes: deliveryPaymentTypes } = delivery;

  let shopPaymentTypes = [];
  let companyPaymentTypes = [];
  if (isQrCode() && qrcode) {
    shopPaymentTypes = qrcode.paymentTypes;
    companyPaymentTypes = qrcode.paymentTypes;
  } else if (customerAddress && orderType === ORDER_TYPE_DELIVERY) {
    companyPaymentTypes = deliveryPaymentTypes;
    shopPaymentTypes =
      shopConfiguration?.delivery?.paymentTypes ?? deliveryPaymentTypes;
  } else {
    companyPaymentTypes = paymentTypes;
    shopPaymentTypes = shopConfiguration.paymentTypes ?? paymentTypes;
  }

  return _.filter(companyPaymentTypes, (paymentType) =>
    shopPaymentTypes.includes(paymentType),
  );
};
